import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { createClient } from '@supabase/supabase-js';
import Navigation from '../../components/Navigation';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Platform {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  features: string;
  image_url?: string;
  image_gallery?: string[];
  rotation_interval?: number;
  enable_rotation?: boolean;
  display_order: number;
  is_active: boolean;
}

interface PlatformFeature {
  id: string;
  platform: string;
  category: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  display_order: number;
  is_active: boolean;
}

// Add ImageCarousel component before the main component
function ImageCarousel({ images, interval = 5000, enableRotation = true, altText }: { 
  images: string[]; 
  interval?: number; 
  enableRotation?: boolean;
  altText: string;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!enableRotation || images.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, interval);

    return () => clearInterval(timer);
  }, [images.length, interval, enableRotation]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  if (images.length === 0) return null;

  return (
    <div className="relative group">
      {/* Main Image Container with 16:9 aspect ratio */}
      <div className="relative w-full rounded-xl overflow-hidden shadow-lg bg-gray-100" style={{ paddingBottom: '56.25%' }}>
        <img 
          src={images[currentIndex]}
          alt={`${altText} - Image ${currentIndex + 1}`}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
        />
        
        {/* Navigation Arrows - Only show if multiple images */}
        {images.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              aria-label="Previous image"
            >
              <i className="ri-arrow-left-s-line text-xl text-gray-800"></i>
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              aria-label="Next image"
            >
              <i className="ri-arrow-right-s-line text-xl text-gray-800"></i>
            </button>
          </>
        )}

        {/* Image Counter */}
        {images.length > 1 && (
          <div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Dots Indicator - Only show if multiple images */}
      {images.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                index === currentIndex 
                  ? 'bg-gray-800 w-8' 
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Platforms() {
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [platformFeatures, setPlatformFeatures] = useState<PlatformFeature[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load platforms
      const { data: platformsData } = await supabase
        .from('platforms')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      // Load platform features
      const { data: featuresData } = await supabase
        .from('platform_features')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      setPlatforms(platformsData || []);
      setPlatformFeatures(featuresData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFeaturesByPlatformAndCategory = (platformName: string, category: string) => {
    return platformFeatures.filter(
      f => f.platform === platformName && f.category === category
    );
  };

  const getPlatformByName = (name: string) => {
    return platforms.find(p => p.name === name);
  };

  const getPlatformImages = (platform: Platform) => {
    const images: string[] = [];
    
    // Add gallery images if available
    if (platform.image_gallery && platform.image_gallery.length > 0) {
      images.push(...platform.image_gallery);
    }
    
    // Add main image as fallback if no gallery images
    if (images.length === 0 && platform.image_url) {
      images.push(platform.image_url);
    }
    
    // Add default image if no images at all
    if (images.length === 0) {
      const defaultImages: Record<string, string> = {
        'Codee': "https://public.readdy.ai/ai/img_res/6f88d66454458039477ec888bfc45978.jpg",
        'Fusion': "https://readdy.ai/api/search-image?query=Data%20flow%20visualization%20dashboard%20showing%20interconnected%20systems%2C%20real-time%20data%20streams%2C%20ETL%20processes%2C%20and%20integration%20pipelines%20with%20purple%20and%20blue%20color%20scheme%20in%20modern%20analytics%20environment&width=600&height=400&seq=fusion-platform&orientation=landscape",
        'BI Platform': "https://readdy.ai/api/search-image?query=Business%20intelligence%20dashboard%20displaying%20colorful%20charts%2C%20graphs%2C%20KPI%20metrics%2C%20and%20data%20visualizations%20on%20large%20monitors%20in%20modern%20corporate%20environment%20with%20green%20accents%20and%20professional%20analytics%20interface&width=600&height=400&seq=bi-platform&orientation=landscape"
      };
      images.push(defaultImages[platform.name] || '');
    }
    
    return images;
  };

  const getColorClasses = (color: string, type: 'bg' | 'text' | 'icon-bg') => {
    const colorMap: Record<string, { bg: string; text: string; iconBg: string }> = {
      blue: { bg: 'bg-blue-600', text: 'text-blue-600', iconBg: 'bg-blue-100' },
      purple: { bg: 'bg-purple-600', text: 'text-purple-600', iconBg: 'bg-purple-100' },
      green: { bg: 'bg-green-600', text: 'text-green-600', iconBg: 'bg-green-100' },
      teal: { bg: 'bg-teal-600', text: 'text-teal-600', iconBg: 'bg-teal-100' },
      indigo: { bg: 'bg-indigo-600', text: 'text-indigo-600', iconBg: 'bg-indigo-100' },
      orange: { bg: 'bg-orange-600', text: 'text-orange-600', iconBg: 'bg-orange-100' },
      pink: { bg: 'bg-pink-600', text: 'text-pink-600', iconBg: 'bg-pink-100' },
      red: { bg: 'bg-red-600', text: 'text-red-600', iconBg: 'bg-red-100' },
    };

    const colors = colorMap[color] || colorMap.blue;
    return type === 'bg' ? colors.bg : type === 'text' ? colors.text : colors.iconBg;
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Platform Suite - Codee, Fusion & BI Solutions",
    "description": "Discover our integrated platform suite: Codee low-code development, Fusion data flow management, and BI analytics platform for enterprise transformation.",
    "url": `${import.meta.env.VITE_SITE_URL}/platforms`,
    "mainEntity": [
      {
        "@type": "SoftwareApplication",
        "name": "Codee Platform",
        "description": "Low Code and No Code platform for rapid application development",
        "applicationCategory": "DeveloperApplication"
      },
      {
        "@type": "SoftwareApplication", 
        "name": "Fusion Platform",
        "description": "Data Flow platform for seamless data integration and management",
        "applicationCategory": "BusinessApplication"
      },
      {
        "@type": "SoftwareApplication",
        "name": "BI Platform", 
        "description": "Business Intelligence platform for data analytics and visualization",
        "applicationCategory": "BusinessApplication"
      }
    ]
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600 mt-4">Loading platforms...</p>
        </div>
      </div>
    );
  }

  const codeePlatform = getPlatformByName('Codee');
  const fusionPlatform = getPlatformByName('Fusion');
  const biPlatform = getPlatformByName('BI Platform');

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Platform Suite - Codee, Fusion & BI Solutions | Allgo Digital"
        description="Discover our integrated platform suite: Codee low-code development, Fusion data flow management, and BI analytics platform for enterprise transformation."
        keywords="platform suite, codee platform, fusion platform, BI platform, low-code development, data integration"
        canonical="/platforms"
        structuredData={structuredData}
      />

      {/* Navigation */}
      <Navigation />

      {/* Back Button */}
      <div className="bg-gray-50 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end">
          <Link to="/" className="inline-flex items-center text-sm sm:text-base text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
            Our Platform Suite
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-blue-100 max-w-3xl mx-auto px-4">
            Discover the power of our three integrated platforms designed to revolutionize your enterprise software development and data management processes.
          </p>
        </div>
      </section>

      {/* Codee Platform */}
      {codeePlatform && (
        <section className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-16">
              <div>
                <div className={`w-16 h-16 sm:w-20 sm:h-20 ${getColorClasses(codeePlatform.color, 'icon-bg')} rounded-xl flex items-center justify-center mb-4 sm:mb-6`}>
                  <i className={`${codeePlatform.icon} text-2xl sm:text-3xl ${getColorClasses(codeePlatform.color, 'text')}`}></i>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                  {codeePlatform.name}
                </h2>
                <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                  {codeePlatform.description}
                </p>
              </div>
              
              <div className="relative">
                <ImageCarousel 
                  images={getPlatformImages(codeePlatform)}
                  interval={codeePlatform.rotation_interval || 5000}
                  enableRotation={codeePlatform.enable_rotation !== false}
                  altText="Codee Low Code Platform Interface"
                />
              </div>
            </div>

            {/* Codee Features */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
              {['AI-Powered Development', 'Collaboration', 'Deployment'].map((category) => {
                const features = getFeaturesByPlatformAndCategory('Codee', category);
                if (features.length === 0) return null;
                return (
                  <div key={category}>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">{category}</h3>
                    <div className="space-y-3 sm:space-y-4">
                      {features.map((feature) => (
                        <div key={feature.id} className={`${getColorClasses(feature.color, 'icon-bg').replace('100', '50')} p-3 sm:p-4 rounded-lg min-h-[120px] sm:h-32 flex flex-col`}>
                          <div className="flex items-center mb-2">
                            <i className={`${feature.icon} ${getColorClasses(feature.color, 'text')} mr-2 sm:mr-3 text-lg sm:text-xl flex-shrink-0`}></i>
                            <h4 className="font-semibold text-sm sm:text-base text-gray-900">{feature.title}</h4>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-600 flex-1">{feature.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Fusion Platform */}
      {fusionPlatform && (
        <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-16">
              <div className="order-2 lg:order-1">
                <ImageCarousel 
                  images={getPlatformImages(fusionPlatform)}
                  interval={fusionPlatform.rotation_interval || 5000}
                  enableRotation={fusionPlatform.enable_rotation !== false}
                  altText="Fusion Data Flow Platform"
                />
              </div>
              
              <div className="order-1 lg:order-2">
                <div className={`w-16 h-16 sm:w-20 sm:h-20 ${getColorClasses(fusionPlatform.color, 'icon-bg')} rounded-xl flex items-center justify-center mb-4 sm:mb-6`}>
                  <i className={`${fusionPlatform.icon} text-2xl sm:text-3xl ${getColorClasses(fusionPlatform.color, 'text')}`}></i>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                  {fusionPlatform.name}
                </h2>
                <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                  {fusionPlatform.description}
                </p>
              </div>
            </div>

            {/* Fusion Features */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
              {['Data Integration', 'Workflow Automation', 'Security'].map((category) => {
                const features = getFeaturesByPlatformAndCategory('Fusion', category);
                if (features.length === 0) return null;
                return (
                  <div key={category}>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">{category}</h3>
                    <div className="space-y-3 sm:space-y-4">
                      {features.map((feature) => (
                        <div key={feature.id} className={`${getColorClasses(feature.color, 'icon-bg').replace('100', '50')} p-3 sm:p-4 rounded-lg min-h-[120px] sm:h-32 flex flex-col`}>
                          <div className="flex items-center mb-2">
                            <i className={`${feature.icon} ${getColorClasses(feature.color, 'text')} mr-2 sm:mr-3 text-lg sm:text-xl flex-shrink-0`}></i>
                            <h4 className="font-semibold text-sm sm:text-base text-gray-900">{feature.title}</h4>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-600 flex-1">{feature.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* BI Platform */}
      {biPlatform && (
        <section className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-16">
              <div>
                <div className={`w-16 h-16 sm:w-20 sm:h-20 ${getColorClasses(biPlatform.color, 'icon-bg')} rounded-xl flex items-center justify-center mb-4 sm:mb-6`}>
                  <i className={`${biPlatform.icon} text-2xl sm:text-3xl ${getColorClasses(biPlatform.color, 'text')}`}></i>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                  {biPlatform.name}
                </h2>
                <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                  {biPlatform.description}
                </p>
              </div>
              
              <div className="relative">
                <ImageCarousel 
                  images={getPlatformImages(biPlatform)}
                  interval={biPlatform.rotation_interval || 5000}
                  enableRotation={biPlatform.enable_rotation !== false}
                  altText="BI Platform Analytics Dashboard"
                />
              </div>
            </div>

            {/* BI Features */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
              {['Analytics', 'Visualization', 'Reporting'].map((category) => {
                const features = getFeaturesByPlatformAndCategory('BI Platform', category);
                if (features.length === 0) return null;
                return (
                  <div key={category}>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">{category}</h3>
                    <div className="space-y-3 sm:space-y-4">
                      {features.map((feature) => (
                        <div key={feature.id} className={`${getColorClasses(feature.color, 'icon-bg').replace('100', '50')} p-3 sm:p-4 rounded-lg min-h-[120px] sm:h-32 flex flex-col`}>
                          <div className="flex items-center mb-2">
                            <i className={`${feature.icon} ${getColorClasses(feature.color, 'text')} mr-2 sm:mr-3 text-lg sm:text-xl flex-shrink-0`}></i>
                            <h4 className="font-semibold text-sm sm:text-base text-gray-900">{feature.title}</h4>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-600 flex-1">{feature.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Integration Benefits */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
            Integrated Platform Benefits
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-8 sm:mb-12 max-w-3xl mx-auto px-4">
            When used together, our platforms create a powerful ecosystem that streamlines your entire software development and data management lifecycle.
          </p>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-speed-line text-xl sm:text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">80% Faster Development</h3>
              <p className="text-sm sm:text-base text-gray-600">
                Reduce development time significantly with our integrated low-code environment and automated data flows.
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-database-2-line text-xl sm:text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Unified Data Management</h3>
              <p className="text-sm sm:text-base text-gray-600">
                Seamlessly connect all your data sources and applications through our integrated platform architecture.
              </p>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <i className="ri-lightbulb-line text-xl sm:text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Real-time Insights</h3>
              <p className="text-sm sm:text-base text-gray-600">
                Get instant visibility into your operations with real-time analytics and automated reporting capabilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6 px-4">
            Ready to Transform Your Enterprise?
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-blue-100 mb-6 sm:mb-8 max-w-3xl mx-auto px-4">
            Experience the power of our integrated platform suite. Start your journey towards faster development, better data management, and actionable insights.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Link to="/partners" className="bg-white text-blue-600 px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Become a Partner
            </Link>
            <Link to="/contact" className="border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Schedule Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <div>
              <img 
                src="https://static.readdy.ai/image/feaac7b19917aec559683ef9ad3d4cd6/f01248d0a1f191b5d9d27b9480b2a552.jpeg" 
                alt="Allgo Digital Logo" 
                className="h-10 sm:h-12 w-auto mb-3 sm:mb-4 filter brightness-0 invert"
              />
              <p className="text-sm sm:text-base text-gray-400">
                Transforming enterprise software development with integrated low-code, data flow, and BI solutions.
              </p>
            </div>
            
            <div>
              <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Platforms</h4>
              <ul className="space-y-2 text-sm sm:text-base text-gray-400">
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">Codee Platform</Link></li>
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">Fusion Platform</Link></li>
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">BI Platform</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Resources</h4>
              <ul className="space-y-2 text-sm sm:text-base text-gray-400">
                <li><Link to="/resources" className="hover:text-white cursor-pointer">Resources</Link></li>
                <li><Link to="/features" className="hover:text-white cursor-pointer">Features</Link></li>
                <li><Link to="/partners" className="hover:text-white cursor-pointer">Partner Program</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Contact</h4>
              <ul className="space-y-2 text-sm sm:text-base text-gray-400">
                <li>info@allgo-digital.com</li>
                <li>+60 3-7931 8888</li>
                <li>Enterprise Solutions</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center text-sm sm:text-base text-gray-400">
            <p>&copy; 2024 Allgo Digital Sdn Bhd. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
